import { useState, useEffect } from 'react';
import { StreamChat, Channel as StreamChatChannel } from 'stream-chat';
import { STREAM_API_KEY } from '@api/constants';

interface UseLivestreamChatReturn {
  chatClient: StreamChat | null;
  channel: StreamChatChannel | null;
  isReady: boolean;
  isLoading: boolean;
  error: string | null;
}

export function useLivestreamChat(chatChannelId: string | null | undefined): UseLivestreamChatReturn {
  const [chatClient, setChatClient] = useState<StreamChat | null>(null);
  const [channel, setChannel] = useState<StreamChatChannel | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!chatChannelId) {
      setIsLoading(false);
      return;
    }

    let mounted = true;
    let client: StreamChat | null = null;
    let shouldCleanup = false;

    const initChat = async () => {
      try {
        setIsLoading(true);
        setError(null);

        client = new StreamChat(STREAM_API_KEY, {
          timeout: 10000,
          enableWSFallback: true,
          allowServerSideConnect: true,
        });
        shouldCleanup = true;

        await client.connectAnonymousUser();

        const livestreamChannel = client.channel('livestream', chatChannelId);
        await livestreamChannel.watch();

        if (mounted) {
          setChatClient(client);
          setChannel(livestreamChannel);
          setIsLoading(false);
        } else {
          await client.disconnectUser().catch(() => {});
        }
      } catch (err) {
        if (mounted) {
          setError(err instanceof Error ? err.message : 'Failed to connect to chat');
          setIsLoading(false);
        }
      }
    };

    initChat();

    return () => {
      mounted = false;
      if (shouldCleanup && client) {
        client.disconnectUser().catch(() => {});
      }
    };
  }, [chatChannelId]);

  return {
    chatClient,
    channel,
    isReady: !isLoading && !!channel && !error,
    isLoading,
    error,
  };
}
